import { ArrowRight, CalendarDays, Clock3, Target } from "lucide-react";
import Link from "next/link";
import type { GuideMetadata } from "@/lib/guides";
import { cn } from "@/lib/utils";

type GuideCardProps = {
  guide: GuideMetadata;
  featured?: boolean;
};

export function GuideCard({ guide, featured = false }: GuideCardProps) {
  const tags = [guide.category, ...guide.level, ...guide.topic].filter(Boolean).slice(0, 5);

  return (
    <Link
      href={`/guides/${guide.slug}`}
      className={cn(
        "group flex h-full min-w-0 flex-col rounded-[24px] border border-slate-200 bg-white p-5 text-left shadow-[0_24px_70px_-48px_rgba(15,23,42,0.58)] ring-1 ring-slate-100 transition-[transform,border-color,box-shadow] duration-150 hover:-translate-y-0.5 hover:border-green-200 hover:shadow-[0_28px_82px_-50px_rgba(8,166,47,0.42)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-green-700 sm:p-6",
        featured && "border-green-200 bg-[#FBFEFA] shadow-[0_28px_82px_-50px_rgba(8,166,47,0.5)]",
      )}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <span className="rounded-full border border-green-100 bg-green-50 px-3 py-1 text-xs font-bold text-[#087D2F]">
          {guide.category}
        </span>
        {guide.readingTime ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-bold text-slate-600">
            <Clock3 className="h-3.5 w-3.5" aria-hidden />
            {guide.readingTime}
          </span>
        ) : null}
      </div>

      <h3 className="mt-4 text-xl font-black leading-tight text-slate-950 group-hover:text-[#087D2F]">
        {guide.shortTitle ?? guide.title}
      </h3>
      <p className="mt-3 line-clamp-3 text-[15px] font-medium leading-7 text-slate-600">
        {guide.description}
      </p>

      {guide.bestFor ? (
        <p className="mt-4 inline-flex items-start gap-2 rounded-2xl bg-green-50 px-3 py-2 text-sm font-bold leading-6 text-slate-700 ring-1 ring-green-100">
          <Target className="mt-0.5 h-4 w-4 shrink-0 text-[#08A62F]" strokeWidth={2.5} aria-hidden />
          <span>Best for: {guide.bestFor}</span>
        </p>
      ) : null}

      {tags.length > 0 ? (
        <div className="mt-5 flex flex-wrap gap-2">
          {tags.slice(1).map((tag) => (
            <span key={tag} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">
              {tag}
            </span>
          ))}
        </div>
      ) : null}

      <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-6">
        {guide.updatedAt ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500">
            <CalendarDays className="h-3.5 w-3.5" aria-hidden />
            Updated {formatGuideDate(guide.updatedAt)}
          </span>
        ) : (
          <span aria-hidden />
        )}
        <span className="inline-flex items-center gap-2 text-sm font-black text-[#087D2F]">
          Read guide
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.6} aria-hidden />
        </span>
      </div>
    </Link>
  );
}

function formatGuideDate(date: string): string {
  const parsed = new Date(`${date}T12:00:00Z`);

  if (Number.isNaN(parsed.getTime())) {
    return date;
  }

  return parsed.toLocaleDateString("en-MA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
